import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CopyIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { RelevoInterface } from '../../../../api/types';
import { ItemsSummaryAccordion } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryAccordion';
import { FailureItem } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryModal';
import { ProcessSpinner } from '../../../../common/components/ProcessSpinner/ProcessSpinner';
import { TableType } from '../../../../common/components/Table/model/enums/table-type.enum';
import { Table } from '../../../../common/components/Table/Table';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useLocalizationContext } from '../../../../contexts/LocalizationContext/useLocalizationContext.hook';
import { useScannedRelevosContext } from '../../../../contexts/ScannedRelevosContext';
import { useReturnItems } from '../../../../services/relevos';
import { AssignItemsConfirmFooter } from '../AssignItemsConfirmFooter/AssignItemsConfirmFooter';
import { ReturnItemsResultStatus, ReturnItemsSuccessBody } from '../ReturnItemsSuccessBody/ReturnItemsSuccessBody';
import { downloadNotAssignedItemsCsv } from './utils/exportErrorListCsv';

type ReturnItemsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ReturnItemsModal = ({ isOpen, onClose }: ReturnItemsModalProps) => {
  const { language } = useLocalizationContext();
  const translations = useTranslations();
  const { restaurantId, scannedRelevos, setScannedRelevos, setAssignedRelevos } = useScannedRelevosContext();
  const {
    mutateAsync: returnItems,
    isLoading: isReturningItems,
    data: itemsReturnedData,
    reset: resetReturnItems,
  } = useReturnItems();
  const [shouldClearScannedAfterClose, setShouldClearScannedAfterClose] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      resetReturnItems();
      setShouldClearScannedAfterClose(false);
    }
  }, [isOpen, resetReturnItems]);

  const executeReturn = async () => {
    if (restaurantId) {
      const relevosUids = scannedRelevos.map((relevo) => relevo.uId);

      try {
        const response = await returnItems({
          restaurantId,
          relevos: relevosUids,
        });

        setAssignedRelevos(response);
        setShouldClearScannedAfterClose(true);
      } catch (error) {
        setShouldClearScannedAfterClose(false);
      }
    }
  };

  const handleCopyUid = useCallback(
    async (uid: string) => {
      try {
        await navigator.clipboard.writeText(uid);
        toast.success(translations('copied_to_clipboard'));
      } catch (e) {
        toast.error(translations('something_went_wrong'));
      }
    },
    [translations],
  );

  const handleDownloadErrorList = useCallback(() => {
    const items = itemsReturnedData?.notAssignedItems || [];
    if (!items.length) return;
    downloadNotAssignedItemsCsv({ items, language, translations });
  }, [itemsReturnedData, language, translations]);

  const handleClose = () => {
    if (shouldClearScannedAfterClose) {
      setScannedRelevos([]);
      setShouldClearScannedAfterClose(false);
    }
    onClose();
  };

  const columns = useMemo<ColumnDef<RelevoInterface>[]>(
    () => [
      {
        id: 'category',
        header: translations('item_category'),
        cell: ({ row }) => row.original.category.translations[language],
      },
      { accessorKey: 'productName', header: translations('item_name') },
    ],
    [language, translations],
  );

  const failureColumns = useMemo<ColumnDef<FailureItem>[]>(
    () => [
      {
        header: translations('item_category'),
        cell: ({ row }) => row.original.product?.category.translations[language],
      },
      {
        id: 'name',
        header: translations('item_name'),
        cell: ({ row }) => row.original.product?.productName,
      },
      {
        id: 'uid',
        header: translations('relevo_uid'),
        cell: ({ row }) => {
          const uid = row.original.product?.uId || '';
          if (!uid) return null;
          return (
            <HStack spacing={1} align="center" justify="center" whiteSpace="nowrap">
              <Text noOfLines={1} title={uid} whiteSpace="nowrap">
                {uid}
              </Text>
              <Tooltip label={translations('copy')}>
                <IconButton
                  aria-label={translations('copy')}
                  variant="ghost"
                  minW="auto"
                  icon={<CopyIcon />}
                  onClick={() => handleCopyUid(uid)}
                />
              </Tooltip>
            </HStack>
          );
        },
      },
      { accessorKey: 'message', header: translations('error') },
    ],
    [language, translations, handleCopyUid],
  );

  const returnedItemsCount = itemsReturnedData?.assignedItems.length ?? scannedRelevos.length ?? 0;
  const notReturnedItemsCount = itemsReturnedData?.notAssignedItems.length ?? 0;
  const totalAttemptedItems = itemsReturnedData ? returnedItemsCount + notReturnedItemsCount : 0;

  let returnResultStatus: ReturnItemsResultStatus | undefined;
  if (itemsReturnedData) {
    if (notReturnedItemsCount === 0) {
      returnResultStatus = 'success';
    } else if (returnedItemsCount === 0) {
      returnResultStatus = 'failure';
    } else {
      returnResultStatus = 'partial';
    }
  }

  return (
    <Modal onClose={handleClose} isOpen={isOpen} isCentered size={'4xl'} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent p={2} display="flex" flexDirection="column">
        {!isReturningItems && !itemsReturnedData ? (
          <>
            <ModalHeader>
              <Text pb={4} fontSize={20}>
                {translations('return_items_confirm')}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={8} flex="1">
              <Table
                type={TableType.SIMPLE}
                variant="relevoSummary"
                data={scannedRelevos}
                columns={columns}
                maxH={'40vh'}
                overflowY="auto"
              />
            </ModalBody>
            <ModalFooter>
              <AssignItemsConfirmFooter onConfirm={executeReturn} onCancel={handleClose} isLoading={isReturningItems}>
                {translations('return_items_confirmed')}
              </AssignItemsConfirmFooter>
            </ModalFooter>
          </>
        ) : null}
        {isReturningItems ? (
          <ModalBody p={2} flex="1" overflowY="auto">
            <ProcessSpinner />
          </ModalBody>
        ) : null}
        {itemsReturnedData ? (
          <>
            <ModalCloseButton />
            <ModalBody flex="1" p={8}>
              <ReturnItemsSuccessBody
                status={returnResultStatus}
                returnedCount={returnedItemsCount}
                totalCount={totalAttemptedItems}
                showProgress={returnResultStatus === 'success'}
                onAnimationComplete={handleClose}
              />
              {!!itemsReturnedData && itemsReturnedData.notAssignedItems?.length ? (
                <ItemsSummaryAccordion
                  buttonText={translations('scanner_returned_items_summary_modal_failure_content')}
                  isOpen={false}
                >
                  <Table
                    type={TableType.SIMPLE}
                    variant="relevoSummary"
                    data={itemsReturnedData.notAssignedItems}
                    columns={failureColumns}
                    maxH={'38vh'}
                    overflowY="auto"
                  />
                  <Flex w="100%" justify="center" mt={4}>
                    <Button variant="orangeSolid" onClick={handleDownloadErrorList}>
                      {translations('download_error_list_csv')}
                    </Button>
                  </Flex>
                </ItemsSummaryAccordion>
              ) : null}
            </ModalBody>
          </>
        ) : null}
      </ModalContent>
    </Modal>
  );
};
